import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * SaveOptionsPopup - Popup when user taps plus/like on a recipe.
 * Options: Add to saved recipes, Add to Meal prep draft.
 * Styled with Tailwind, Mob-style.
 */
const SaveOptionsPopup = ({
    show,
    onClose,
    recipeId,
    recipeName,
    isSaved,
    isInMealPrepDraft,
    onAddToSaved,
    onRemoveFromSaved,
    onAddToMealPrepDraft,
    onRemoveFromMealPrepDraft,
}) => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [addingSaved, setAddingSaved] = useState(false);
    const [addingDraft, setAddingDraft] = useState(false);

    useEffect(() => {
        if (show) {
            setTimeout(() => setIsVisible(true), 10);
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            setIsVisible(false);
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [show]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 280);
    };

    const handleAddToSaved = async (e) => {
        e.stopPropagation();
        if (addingSaved) return;
        setAddingSaved(true);
        try {
            if (isSaved && onRemoveFromSaved) await onRemoveFromSaved();
            else if (onAddToSaved) await onAddToSaved();
            handleClose();
        } finally {
            setAddingSaved(false);
        }
    };

    const handleAddToMealPrepDraft = async (e) => {
        e.stopPropagation();
        if (addingDraft) return;
        setAddingDraft(true);
        try {
            if (isInMealPrepDraft && onRemoveFromMealPrepDraft) await onRemoveFromMealPrepDraft();
            else if (onAddToMealPrepDraft) await onAddToMealPrepDraft();
            handleClose();
        } finally {
            setAddingDraft(false);
        }
    };

    const goToMealPrepDraft = () => {
        handleClose();
        navigate('/meal-prep-draft');
    };

    const goToSavedRecipes = () => {
        handleClose();
        navigate('/savedrecipes');
    };

    if (!show) return null;

    return (
        <div
            className={`fixed inset-0 z-[10001] flex items-center justify-center p-4 transition-opacity duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'} ${isVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ backgroundColor: isVisible ? 'rgba(0,0,0,0.6)' : 'transparent', backdropFilter: isVisible ? 'blur(6px)' : 'none' }}
            onClick={handleClose}
        >
            <div
                className={`relative w-full max-w-[380px] rounded-2xl bg-white p-6 shadow-xl transition-all duration-300 ease-out ${isVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-5 scale-95 opacity-0'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="text-center mb-6">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700">
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 tracking-tight">Save recipe</h2>
                    {recipeName && (
                        <p className="mt-1 text-sm text-gray-500 line-clamp-2 leading-snug">{recipeName}</p>
                    )}
                </div>

                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={handleAddToSaved}
                        disabled={addingSaved}
                        className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-semibold transition-colors disabled:opacity-80 ${isSaved ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700'}`}
                    >
                        <span className="flex shrink-0 text-current">
                            <svg className="h-5 w-5" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </span>
                        <span className="flex-1">{isSaved ? 'Remove from saved recipes' : 'Add to saved recipes'}</span>
                        {addingSaved && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />}
                    </button>

                    <button
                        type="button"
                        onClick={handleAddToMealPrepDraft}
                        disabled={addingDraft}
                        className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-semibold transition-colors disabled:opacity-80 ${isInMealPrepDraft ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-700 hover:border-amber-500 hover:bg-amber-50 hover:text-amber-800'}`}
                    >
                        <span className="flex shrink-0 text-current">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </span>
                        <span className="flex-1">{isInMealPrepDraft ? 'Remove from Meal prep draft' : 'Add to Meal prep draft'}</span>
                        {addingDraft && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />}
                    </button>
                </div>

                <div className="mt-5 flex flex-wrap justify-center gap-3 border-t border-gray-100 pt-4">
                    <button type="button" onClick={goToSavedRecipes} className="text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg px-2.5 py-1.5 transition-colors">
                        View saved recipes
                    </button>
                    <button type="button" onClick={goToMealPrepDraft} className="text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg px-2.5 py-1.5 transition-colors">
                        View meal prep draft
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaveOptionsPopup;
